import type { Consent, ConsentModel, ConsentAction } from './types';

const initialState = (): ConsentModel => ({
  consents: [],
  isFetching: false,
  isUpdating: false,

  /**
   * TODO: Clarify naming of this flag https://workivate.atlassian.net/browse/UI-4671
   * shouldResumePostLoginFlow - Controls whether post-login flow (consents/walkthrough) should be DEFERRED
   *
   * ⚠️ CONFUSING NAME ALERT: Despite the name, this is actually a "defer/hide flag" not a "resume flag"
   *
   * Think of it as: "hideConsentsAfterLogin" or "deferPostLoginFlow"
   *
   * Values:
   * - false (default) = Show consents/walkthrough normally after login
   * - true = HIDE/DEFER consents/walkthrough (user is in signup flow)
   *
   * Mental Model:
   *   shouldResumePostLoginFlow = true  →  "Hide consents, user needs to complete signup first"
   *   shouldResumePostLoginFlow = false →  "Show consents normally"
   *
   * Usage:
   * - Set to TRUE during SSO signup flows (signup_state: 50) to prevent consents showing before signup form
   * - Set to FALSE after signup is complete to allow normal post-login flow
   *
   * Why the name is backwards:
   * - "shouldResume" suggests TRUE = resume/continue flow, but it's actually the opposite
   * - TRUE means "defer/pause/HIDE the flow", FALSE means "proceed with flow"
   * - Better names: shouldDeferPostLoginFlow | hideConsentsAfterLogin | isPostLoginFlowPaused
   *
   * Why default FALSE is correct:
   * - Most users are NOT in a signup flow, so consents should show normally
   * - Only SSO auto-provisioned users (signup_state: 50) need to defer
   * - This flag is explicitly set to TRUE only during those specific flows
   */
  shouldResumePostLoginFlow: false,
  redirectionState: null,
  lastUpdated: null,
});

export const consentReducer = (
  state: ConsentModel = initialState(),
  action: ConsentAction,
): ConsentModel => {
  switch (action.type) {
    case 'CONSENT/RESET':
      return {
        ...initialState(),
      };
    case 'CONSENT/GET_ALL_CONSENTS':
      return {
        ...state,
        consents: action.payload,
        lastUpdated: new Date().getTime(),
      };
    case 'CONSENT/SET_IS_FETCHING_STATUS':
      return { ...state, isFetching: action.payload };
    case 'CONSENT/SET_IS_UPDATING_STATUS':
      return { ...state, isUpdating: action.payload };
    case 'CONSENT/SET_SHOULD_RESUME_POST_LOGIN_FLOW':
      return { ...state, shouldResumePostLoginFlow: action.payload };
    case 'CONSENT/SET_REDIRECTION_MODEL':
      return { ...state, redirectionState: action.payload };
    case 'CONSENT/UPDATE_CONSENT':
      const newState = state;
      newState.consents = state.consents.map((consent: Consent) => {
        if (consent['consent_type'] === action.payload['consent_type']) {
          return action.payload;
        }

        return consent;
      });

      return { ...newState };

    default:
      return state;
  }
};
